'use client';

import { useEffect, useState } from 'react';
import { Building2, ChevronDown, Gauge, Layers, LayoutDashboard, PanelLeftClose, PanelLeftOpen, Settings, ShieldCheck, UserRound, Users } from 'lucide-react';
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { CommandPalette } from '@/components/dashboard/CommandPalette';
import { Header } from '@/components/dashboard/Header';
import { LogoutModal } from '@/components/dashboard/LogoutModal';
import type { CommandEntry, DashboardUser } from '@/components/dashboard/types';
import { useDashboardTheme } from '@/components/dashboard/useDashboardTheme';
import { PlatformScopeBanner } from '@/components/platform/ScopeBanner';
import { api } from '@/lib/api';
import { currentStaff } from '@/lib/auth-client';
import { parsePlatformControl, type PlatformControl } from '@/lib/platform/control';
import { CONTROL_GROUPS, CONTROL_SECTIONS, controlSectionAlert, controlSectionFromPath, controlSectionPath } from '@/lib/platform/control-nav';

const SIDEBAR_KEY = 'fleetava-sidebar-collapsed';
const TOAST_MS = 3200;

const LINKS = [
  { href: '/super-admin', label: 'Overview', icon: LayoutDashboard, end: true },
  { href: '/super-admin/agencies', label: 'Agencies', icon: Building2, end: false },
  { href: '/super-admin/plans', label: 'Plans', icon: Layers, end: false },
  { href: '/super-admin/users', label: 'Platform users', icon: Users, end: false },
  { href: '/super-admin/staff', label: 'Agency staff', icon: ShieldCheck, end: false },
  { href: '/super-admin/profile', label: 'Profile', icon: UserRound, end: true },
] as const;

const COMMANDS: CommandEntry[] = [
  ...LINKS.map((link) => ({
    id: link.href,
    label: link.label,
    group: 'Pages',
    href: link.href,
  })),
  ...CONTROL_SECTIONS.map((section) => ({
    id: controlSectionPath(section.id),
    label: `Control · ${section.label}`,
    group: 'Control',
    href: controlSectionPath(section.id),
  })),
  { id: 'new-agency', label: 'New agency', group: 'Pages', href: '/super-admin/agencies/new' },
];

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_KEY) === '1';
  } catch {
    return false;
  }
}

function pageTitle(pathname: string): string {
  if (pathname === '/super-admin') return 'Overview';
  if (pathname === '/super-admin/agencies/new') return 'New agency';
  if (pathname === '/super-admin/agencies') return 'Agencies';
  if (pathname.startsWith('/super-admin/agencies/')) return 'Agency';
  if (pathname.startsWith('/super-admin/staff')) return 'Agency staff';
  if (pathname.startsWith('/super-admin/audit')) return 'Audit';
  if (pathname.startsWith('/super-admin/plans')) return 'Plans';
  if (pathname.startsWith('/super-admin/users')) return 'Platform users';
  if (pathname.startsWith('/super-admin/settings')) {
    const section = controlSectionFromPath(pathname);
    const label = CONTROL_SECTIONS.find((item) => item.id === section)?.label;
    return label ?? 'Control';
  }
  if (pathname.startsWith('/super-admin/profile')) return 'Profile';
  return 'Platform';
}

export function PlatformConsole() {
  const session = currentStaff();
  const pathname = useLocation().pathname;
  const navigate = useNavigate();
  const { theme, toggleTheme } = useDashboardTheme();
  const controlRoute = pathname.startsWith('/super-admin/settings');
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [controlOpen, setControlOpen] = useState(true);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; tone: 'ok' | 'error' } | null>(null);

  useEffect(() => {
    if (controlRoute) setControlOpen(true);
  }, [controlRoute]);

  const controlMenuOpen = controlOpen && !collapsed;

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen(true);
      }
      if (event.key === 'Escape') setPaletteOpen(false);
    }
    function onToast(event: Event) {
      const detail = (event as CustomEvent<{ message?: unknown; tone?: unknown }>).detail;
      if (typeof detail?.message !== 'string') return;
      setToast({ message: detail.message, tone: detail.tone === 'error' ? 'error' : 'ok' });
      window.setTimeout(() => setToast(null), TOAST_MS);
    }
    window.addEventListener('keydown', onKey);
    window.addEventListener('platform-toast', onToast);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('platform-toast', onToast);
    };
  }, []);

  if (!session || session.user.role !== 'platform_admin' || session.user.scope !== 'platform') {
    return <Navigate to="/login" replace />;
  }

  const user: DashboardUser = {
    name: session.user.name,
    email: session.user.email,
    role: 'platform_admin',
  };

  function toggleSidebar() {
    setCollapsed((current) => {
      const next = !current;
      window.localStorage.setItem(SIDEBAR_KEY, next ? '1' : '0');
      return next;
    });
  }

  if (pathname.endsWith('/site-builder')) {
    return (
      <div data-theme={theme} className="dashboard-root h-dvh overflow-hidden bg-shell text-ink">
        <Outlet />
        {toast && (
          <div
            role="status"
            className={`fixed bottom-4 right-4 z-50 max-w-sm rounded-xl border bg-card px-4 py-3 text-sm shadow-xl ${toast.tone === 'error' ? 'border-danger/40 text-danger' : 'border-line text-ink'}`}
          >
            {toast.message}
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-theme={theme} className="dashboard-root min-h-dvh bg-shell text-ink">
      <PlatformScopeBanner />
      <PlatformNav
        collapsed={collapsed}
        email={user.email}
        controlOpen={controlOpen}
        onToggleControl={() => setControlOpen((current) => !current)}
        onToggle={toggleSidebar}
      />
      <div className={collapsed ? 'lg:pl-16' : controlMenuOpen ? 'lg:pl-64' : 'lg:pl-60'}>
        <Header
          title={pageTitle(pathname)}
          user={user}
          theme={theme}
          onToggleTheme={toggleTheme}
          onSearch={() => setPaletteOpen(true)}
          onSignOut={() => setLogoutOpen(true)}
          onEditProfile={() => navigate('/super-admin/profile')}
        />
        <main className="px-4 py-6 sm:px-6">
          <Outlet />
        </main>
      </div>
      <CommandPalette
        open={paletteOpen}
        entries={COMMANDS}
        onClose={() => setPaletteOpen(false)}
        onGo={(href) => navigate(href)}
      />
      <LogoutModal open={logoutOpen} onClose={() => setLogoutOpen(false)} />
      {toast && (
        <div
          role="status"
          className={`fixed bottom-4 right-4 z-50 max-w-sm rounded-xl border bg-card px-4 py-3 text-sm shadow-xl ${toast.tone === 'error' ? 'border-danger/40 text-danger' : 'border-line text-ink'}`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}

function PlatformNav({
  collapsed,
  email,
  controlOpen,
  onToggleControl,
  onToggle,
}: {
  collapsed: boolean;
  email: string;
  controlOpen: boolean;
  onToggleControl: () => void;
  onToggle: () => void;
}) {
  const pathname = useLocation().pathname;
  const controlRoute = pathname.startsWith('/super-admin/settings');
  const [control, setControl] = useState<PlatformControl | null>(null);
  const profile = LINKS[LINKS.length - 1];
  const primary = LINKS.slice(0, -1);

  useEffect(() => {
    if (!controlOpen) return;
    let cancelled = false;
    api<unknown>('/api/platform/control')
      .then((payload) => {
        if (!cancelled) setControl(parsePlatformControl(payload));
      })
      .catch(() => {
        if (!cancelled) setControl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [controlOpen, pathname]);

  return (
    <aside
      className={`border-b border-line bg-shell lg:fixed lg:bottom-0 lg:top-9 lg:flex lg:flex-col lg:border-b-0 lg:border-r ${collapsed ? 'lg:w-16' : controlOpen ? 'lg:w-64' : 'lg:w-60'}`}
    >
      <div className={`flex items-center gap-2 px-4 py-5 ${collapsed ? 'lg:justify-center lg:px-2' : ''}`}>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand text-slate-950">
          <Gauge size={18} />
        </span>
        {!collapsed && (
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink">
              fleetava
            </p>
            <p className="truncate text-[11px] text-muted">{email}</p>
          </div>
        )}
      </div>
      <nav className="flex flex-wrap gap-1 px-3 pb-3 lg:block lg:flex-1 lg:space-y-1 lg:overflow-y-auto">
        {primary.map((item) => (
          <SidebarLink key={item.href} item={item} collapsed={collapsed} />
        ))}
        <div className="w-full lg:w-auto">
          <div className={`flex items-center ${collapsed ? 'lg:justify-center' : ''}`}>
            <NavLink
              to="/super-admin/settings"
              end
              title="Control"
              className={`flex min-w-0 flex-1 items-center gap-2 rounded-xl border-l-2 px-3 py-2 text-sm font-medium transition-all ${
                controlRoute ? 'border-brand text-ink' : 'border-transparent text-muted hover:bg-card hover:text-ink'
              } ${collapsed ? 'lg:flex-none lg:justify-center lg:px-2' : ''}`}
            >
              <Settings size={16} />
              <span className={collapsed ? 'lg:sr-only' : ''}>Control</span>
            </NavLink>
            <button
              type="button"
              aria-expanded={controlOpen}
              aria-label={controlOpen ? 'Collapse control menu' : 'Expand control menu'}
              onClick={onToggleControl}
              className={`rounded-lg p-1.5 text-muted hover:text-ink ${collapsed ? 'lg:hidden' : ''}`}
            >
              <ChevronDown size={14} className={controlOpen ? 'rotate-180' : ''} />
            </button>
          </div>
          {controlOpen && (
            <div className={`mt-1 space-y-3 border-l border-line pl-3 ${collapsed ? 'lg:hidden' : ''}`}>
              {CONTROL_GROUPS.map((group) => (
                <div key={group}>
                  <p className="type-table-header px-2 text-muted">{group}</p>
                  <div className="mt-1 space-y-0.5">
                    {CONTROL_SECTIONS.filter((section) => section.group === group).map((section) => {
                      const alerts = controlSectionAlert(section.id, control);
                      const href = controlSectionPath(section.id);
                      const active = controlSectionFromPath(pathname) === section.id;
                      return (
                        <NavLink
                          key={section.id}
                          to={href}
                          end={section.id === 'overview'}
                          className={`flex items-center justify-between rounded-lg px-2 py-1.5 text-sm ${active ? 'bg-brand/15 font-semibold text-ink' : 'text-muted hover:bg-card hover:text-ink'}`}
                        >
                          <span>{section.label}</span>
                          {alerts > 0 && <span className="rounded-full bg-warning/15 px-1.5 text-[11px] font-semibold text-warning">{alerts}</span>}
                        </NavLink>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {profile && <SidebarLink item={profile} collapsed={collapsed} />}
      </nav>
      <button
        type="button"
        onClick={onToggle}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        className="m-3 hidden items-center justify-center rounded-xl border border-line p-2 text-muted transition-colors hover:text-ink lg:inline-flex"
      >
        {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
      </button>
    </aside>
  );
}

function SidebarLink({
  item,
  collapsed,
}: {
  item: (typeof LINKS)[number];
  collapsed: boolean;
}) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.href}
      end={item.end}
      title={item.label}
      className={({ isActive }) =>
        `flex items-center gap-2 rounded-xl border-l-2 px-3 py-2 text-sm font-medium transition-all ${
          isActive ? 'border-brand bg-brand/15 text-ink' : 'border-transparent text-muted hover:bg-card hover:text-ink'
        } ${collapsed ? 'lg:justify-center lg:px-2' : ''}`
      }
    >
      <Icon size={16} />
      <span className={collapsed ? 'lg:sr-only' : ''}>{item.label}</span>
    </NavLink>
  );
}
